import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, toDisplayString as _toDisplayString, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, withModifiers as _withModifiers, Transition as _Transition, withCtx as _withCtx, createVNode as _createVNode } from "vue"

const _hoisted_1 = { class: "w-full bg-white border-b border-gray-100 shadow-sm sticky top-0 z-50" }
const _hoisted_2 = { class: "max-w-screen-4xl mx-auto flex items-stretch overflow-hidden h-16 lg:h-16" }
const _hoisted_3 = { class: "flex-1 flex items-stretch bg-gray-100 gap-px overflow-x-auto no-scrollbar" }
const _hoisted_4 = { class: "flex w-full justify-between items-center space-x-2 lg:space-x-4 pl-4" }
const _hoisted_5 = ["src"]
const _hoisted_6 = { class: "flex flex-col min-w-0 grow" }
const _hoisted_7 = ["onClick"]
const _hoisted_8 = { class: "flex w-full justify-between items-center space-x-2 lg:space-x-4 pl-4" }
const _hoisted_9 = ["src"]
const _hoisted_10 = ["src"]
const _hoisted_11 = { class: "flex flex-col min-w-0 grow" }
const _hoisted_12 = ["onClick", "title"]
const _hoisted_13 = { class: "flex w-full items-center space-x-2 lg:space-x-4 pl-4" }
const _hoisted_14 = { class: "flex flex-col min-w-0 grow" }
const _hoisted_15 = { class: "text-[9px] lg:text-[11px] font-bold uppercase tracking-wider truncate text-gray-400 group-hover:text-gray-600 transition-colors" }
const _hoisted_16 = { class: "text-xl font-ivymode mb-4 text-gray-900" }
const _hoisted_17 = { class: "text-gray-600 mb-8 font-light italic" }
const _hoisted_18 = { class: "space-y-3" }

import {computed, ref} from "vue";
import {useStore} from "vuex";


export default /*@__PURE__*/_defineComponent({
  __name: 'stepWidgetConf',
  props: {
  session: {
    type: Object,
    required: true
  },
  itemIdx: {
    type: Number,
    default: 0
  }
},
  emits: ['delete-item', 'add-item'],
  setup(__props, { emit: __emit }) {

/**
 * Wedding ring step widget (used when the wedding ring configuration - itemV2VariantsConf - is active).
 *
 * Unlike stepWidget.vue, this widget has no overview picture on the left. Instead it always shows
 * one step for the set-view plus one step for every item that is in the session.
 *
 * Clicking the set-view step does not navigate away. Instead it stays on the current view and sets
 * the app-wide "configureAll" flag (session/configureAll), which components (e.g. the variants list)
 * can use to show the set price instead of the single item price. Clicking an item step selects that
 * item and clears "configureAll" so only that item's price and variants are shown.
 *
 * If the session only contains a single item, an additional "add item" step is rendered. Clicking
 * it emits "add-item" with the next available item index (session.items.length), so the parent
 * component can call the backend to add a new item to the session.
 */
const store = useStore();

const props = __props;

const emit = __emit;

const configureAll = computed(() => store.getters["session/getConfigureAll"]);

const setPrice = computed(() => {
  let total = 0;
  for (const item of props.session.items || []) {
    total += item.price?.total || 0;
  }
  return total;
});

const selectSetView = () => {
  store.commit("session/setConfigureAll", true);
}

const selectItem = (idx: number) => {
  store.commit("session/setConfigureAll", false);
  store.commit("session/setItemIdx", idx);
}

const getItemName = (idx: number) => {
  const persons = props.session.persons || [];
  const person = persons.find((p: any) => p.items?.includes(idx));
  return person?.name || '';
};

const getItemSize = (item: any) => {
  const variants = item.variants || {};
  for (const variantIdx in variants) {
    const variantData = variants[variantIdx];
    if (variantData?.idx === 'size') {
      return variantData.text
    }
  }
  return '';
};

const showDeleteModal = ref(false);
const itemIdxToDelete = ref<number | null>(null);

const confirmDelete = (idx: number) => {
  itemIdxToDelete.value = idx;
  showDeleteModal.value = true;
};

const onConfirmDelete = () => {
  if (itemIdxToDelete.value !== null) {
    emit('delete-item', itemIdxToDelete.value);
    showDeleteModal.value = false;
    itemIdxToDelete.value = null;
  }
};

const addItem = () => {
  emit('add-item', props.session.items.length);
};

const setImage = computed(() => {
  let setImage = ""

  if (props.session.items.length == 1) {
    setImage = props.session.items[0].image.filename
  } else {

    for (let i = 0; i < props.session.items.length; i++) {
      if (setImage == "") {
        setImage = props.session.items[i].image.filename.replace("item/image/", "item/image/set/")
      } else {
        const filenameParts = props.session.items[i].image.filename.split("/")
        const itemHash = filenameParts[filenameParts.length - 1]

        setImage += `/${itemHash}`
      }
    }
  }

  return setImage
});


return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _createElementVNode("div", {
          class: _normalizeClass(["flex-1 flex items-center justify-center px-2 lg:px-6 relative cursor-pointer group transition-all duration-300 step-container min-w-[140px] lg:min-w-[200px]", {'active': configureAll.value, 'completed': configureAll.value}]),
          onClick: selectSetView
        }, [
          _createElementVNode("div", _hoisted_4, [
            _createElementVNode("div", {
              class: _normalizeClass(["w-7 h-7 lg:w-12 lg:h-12 rounded-full flex items-center justify-center border-2 transition-all duration-300 shrink-0", [configureAll.value ? 'border-white bg-white text-black' : 'border-gray-100 text-gray-300 group-hover:border-gray-200']])
            }, [
              _createElementVNode("img", {
                src: setImage.value,
                class: "w-full h-full object-cover",
                alt: ""
              }, null, 8, _hoisted_5)
            ], 2),
            _createElementVNode("div", _hoisted_6, [
              _createElementVNode("span", {
                class: _normalizeClass(["text-[9px] lg:text-[11px] font-bold uppercase tracking-wider truncate transition-colors", configureAll.value ? 'text-black font-bold' : 'text-gray-400 group-hover:text-gray-600'])
              }, _toDisplayString(_ctx.$t('components.itemV2.itemV2Main.set_view')), 3),
              _createElementVNode("span", {
                class: _normalizeClass(["text-[8px] lg:text-[10px] font-medium truncate", configureAll.value ? 'text-black' : 'text-brand-main'])
              }, _toDisplayString(_ctx.$n(setPrice.value, 'currency')), 3)
            ])
          ])
        ], 2),
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.session.items, (item, idx) => {
          return (_openBlock(), _createElementBlock("div", {
            key: item.item_number || idx,
            class: _normalizeClass(["flex-1 flex items-center justify-center px-2 lg:px-6 relative cursor-pointer group transition-all duration-300 step-container min-w-[140px] lg:min-w-[200px]", {'active': !configureAll.value && __props.itemIdx == idx, 'completed': !configureAll.value && __props.itemIdx == idx}]),
            onClick: ($event: any) => (selectItem(idx))
          }, [
            _createElementVNode("div", _hoisted_8, [
              _createElementVNode("div", {
                class: _normalizeClass(["w-7 h-7 lg:w-12 lg:h-12 rounded-full bg-white overflow-hidden border-2 transition-all duration-300 shrink-0 relative", [!configureAll.value && __props.itemIdx == idx ? 'border-white' : 'border-gray-100 group-hover:border-gray-200']])
              }, [
                (item.image?.filename_layer)
                  ? (_openBlock(), _createElementBlock("img", {
                      key: 0,
                      src: item.image.filename_layer,
                      class: "absolute insert-0 w-auto h-full aspect-square object-cover",
                      alt: "Setting image"
                    }, null, 8, _hoisted_9))
                  : _createCommentVNode("", true),
                _createElementVNode("img", {
                  src: item.image?.filename,
                  class: "w-full h-full object-cover",
                  alt: ""
                }, null, 8, _hoisted_10)
              ], 2),
              _createElementVNode("div", _hoisted_11, [
                _createElementVNode("span", {
                  class: _normalizeClass(["text-[9px] lg:text-[11px] font-bold uppercase tracking-wider truncate transition-colors", !configureAll.value && __props.itemIdx == idx ? 'text-black font-bold' : 'text-gray-400 group-hover:text-gray-600'])
                }, [
                  _createTextVNode(_toDisplayString(getItemName(parseInt(idx.toString())) || _ctx.$t('components.stepWidget.setting.ring')) + " ", 1),
                  (!getItemName(parseInt(idx.toString())) && __props.session.items.length > 1)
                    ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                        _createTextVNode(_toDisplayString(idx + 1), 1)
                      ], 64))
                    : _createCommentVNode("", true),
                  (getItemSize(item))
                    ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                        _createTextVNode(" (" + _toDisplayString(getItemSize(item)) + ")", 1)
                      ], 64))
                    : _createCommentVNode("", true)
                ], 2),
                _createElementVNode("span", {
                  class: _normalizeClass(["text-[8px] lg:text-[10px] font-medium truncate", !configureAll.value && __props.itemIdx == idx ? 'text-black' : 'text-brand-main'])
                }, _toDisplayString(_ctx.$n(item.price?.total || 0, 'currency')), 3)
              ]),
              _createElementVNode("button", {
                onClick: _withModifiers(($event: any) => (confirmDelete(idx)), ["stop"]),
                title: _ctx.$t('components.itemV2.itemV2ItemWidget.remove'),
                class: "shrink-0 p-1 text-gray-500 hover:text-brand-main transition-colors cursor-pointer"
              }, [...(_cache[3] || (_cache[3] = [
                _createElementVNode("svg", {
                  xmlns: "http://www.w3.org/2000/svg",
                  fill: "none",
                  viewBox: "0 0 24 24",
                  "stroke-width": "1.5",
                  stroke: "currentColor",
                  class: "w-4 h-4 lg:w-5 lg:h-5"
                }, [
                  _createElementVNode("path", {
                    "stroke-linecap": "round",
                    "stroke-linejoin": "round",
                    d: "m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0"
                  })
                ], -1)
              ]))], 8, _hoisted_12)
            ])
          ], 10, _hoisted_7))
        }), 128)),
        (__props.session.items.length === 1)
          ? (_openBlock(), _createElementBlock("div", {
              key: 0,
              class: "flex-1 flex items-center justify-center px-2 lg:px-6 relative cursor-pointer group transition-all duration-300 step-container min-w-[140px] lg:min-w-[200px]",
              onClick: addItem
            }, [
              _createElementVNode("div", _hoisted_13, [
                _cache[4] || (_cache[4] = _createElementVNode("div", { class: "w-7 h-7 lg:w-12 lg:h-12 rounded-full flex items-center justify-center border-2 border-dashed border-gray-200 text-gray-300 shrink-0 group-hover:border-gray-300 group-hover:text-gray-400 transition-all duration-300" }, [
                  _createElementVNode("span", { class: "text-xs lg:text-sm font-bold" }, "+")
                ], -1)),
                _createElementVNode("div", _hoisted_14, [
                  _createElementVNode("span", _hoisted_15, _toDisplayString(_ctx.$t('components.itemV2.itemV2Main.add_item')), 1)
                ])
              ])
            ]))
          : _createCommentVNode("", true)
      ])
    ]),
    _createVNode(_Transition, { name: "fade" }, {
      default: _withCtx(() => [
        (showDeleteModal.value)
          ? (_openBlock(), _createElementBlock("div", {
              key: 0,
              class: "fixed inset-0 z-[100] flex items-center justify-center bg-black/30 backdrop-blur-sm px-4",
              onClick: _cache[2] || (_cache[2] = ($event: any) => (showDeleteModal.value = false))
            }, [
              _createElementVNode("div", {
                class: "bg-white p-8 max-w-sm w-full shadow-2xl border border-gray-100",
                onClick: _cache[1] || (_cache[1] = _withModifiers(() => {}, ["stop"]))
              }, [
                _createElementVNode("h3", _hoisted_16, _toDisplayString(_ctx.$t('components.itemV2.itemV2ItemWidget.remove_confirm_title')), 1),
                _createElementVNode("p", _hoisted_17, _toDisplayString(_ctx.$t('components.itemV2.itemV2ItemWidget.remove_confirm_text')), 1),
                _createElementVNode("div", _hoisted_18, [
                  _createElementVNode("button", {
                    onClick: onConfirmDelete,
                    class: "block w-full text-white bg-brand-main p-2 text-center hover:bg-white hover:text-brand-main border border-brand-main text-sm font-light transition-colors"
                  }, _toDisplayString(_ctx.$t('components.itemV2.itemV2ItemWidget.confirm')), 1),
                  _createElementVNode("button", {
                    onClick: _cache[0] || (_cache[0] = ($event: any) => (showDeleteModal.value = false)),
                    class: "block w-full text-brand-main bg-white p-2 text-center hover:bg-brand-gray border border-brand-main text-sm font-light transition-colors"
                  }, _toDisplayString(_ctx.$t('components.itemV2.itemV2ItemWidget.cancel')), 1)
                ])
              ])
            ]))
          : _createCommentVNode("", true)
      ]),
      _: 1
    })
  ]))
}
}

})